import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { Item, User } from '../types'
import { utcToZonedTime } from 'date-fns-tz'
import { add, intervalToDuration } from 'date-fns'

export type DeliveryEstimate = {
	restaurantId: string
	userAddress: string
	estimate: {
		fee: number
		timeToDeliver: number
		totalFee: number
		gst?: number
		pst?: number
		deliveryTax?: number
	}
}
// persisted data is maintained on refresh / app close
const initialState = {
	cartPrep: {
		prepTimeDuration: '',
		pickupTime: '',
		deliveryTime: '',
		isScheduled: false,
		longestPrepTime: 15,
		deliveryPrepTime: 15,
		pickupScheduled: false,
		deliveryScheduled: false,
	},
	cart: [] as Item[],
	isDelivery: false,
	restaurantLocation: {} as any,
	showLocationModal: false,
	showPickupPointModal: false,
	showDeliveryModal: false,
	pickupPointModalVisible: false,
	selectedLocation: {} as any,
	activeRID: '',
	selectedPickupPoint: {} as any,
	contactlessDropoff: true,
	deliveryEstimate: {
		restaurantId: '',
		estimate: {
			fee: -1,
			timeToDeliver: -1,
			totalFee: -1,
			gst: -1,
			pst: -1,
		},
	} as DeliveryEstimate,
	deliveryEstimateList: [] as DeliveryEstimate[],
	redeemedRewards: [] as any[],
	isTestMode: false,
	receiptPrinter: null as any,
	printerType: '',
	navigationStack: null as any,
	couponCode: '',
	couponCodeData: [] as any[],
	couponCodeDisplay: null as any,
	couponCodeBackup: '',
	couponRefresh: [] as any[],
	visibleCouponCount: 0,
	validCouponCodes: [] as any[],
	couponName: '',
	restaurantDiscountAmount: null as any,
}

type StateTypes = typeof initialState

const slice = createSlice({
	name: 'persisted',
	initialState,
	reducers: {
		setStates: (state, action: PayloadAction<Partial<StateTypes>>) => {
			Object.assign(state, action.payload)
		},
		addItem: (state, action: PayloadAction<Item>) => {
			if (!state.cart) {
				state.cart = []
			}

			// Check if we have the same item with same selected modifiers
			const existingItemIndex = state.cart.findIndex(item => {
				// Get selected modifier IDs for both items
				const itemSelectedMods = item.modList
					.flatMap(mod => mod.options.filter(opt => opt.isSelected).map(opt => opt.id))
					.sort()

				const payloadSelectedMods = action.payload.modList
					.flatMap(mod => mod.options.filter(opt => opt.isSelected).map(opt => opt.id))
					.sort()

				// Compare arrays of selected modifier IDs
				return (
					item.id === action.payload.id &&
					JSON.stringify(itemSelectedMods) === JSON.stringify(payloadSelectedMods)
				)
			})

			if (existingItemIndex !== -1) {
				// Update existing item's quantity and other properties
				state.cart[existingItemIndex] = {
					...state.cart[existingItemIndex],
					qty: state.cart[existingItemIndex].qty + action.payload.qty,
					// Preserve the original idempotencyKey and timeAdded
					idempotencyKey: state.cart[existingItemIndex].idempotencyKey,
					timeAdded: state.cart[existingItemIndex].timeAdded,
				}
			} else {
				// Add as new item if no matching item found
				state.cart.push(action.payload)
			}
		},
		updateItem: (state, action: PayloadAction<Item>) => {
			// First check if we have the same item with same selected modifiers but different idempotencyKey
			const duplicateItemIndex = state.cart.findIndex(item => {
				// Get selected modifier IDs for both items
				const itemSelectedMods = item.modList
					.flatMap(mod => mod.options.filter(opt => opt.isSelected).map(opt => opt.id))
					.sort()

				const payloadSelectedMods = action.payload.modList
					.flatMap(mod => mod.options.filter(opt => opt.isSelected).map(opt => opt.id))
					.sort()

				// Compare arrays of selected modifier IDs
				return (
					item.id === action.payload.id &&
					JSON.stringify(itemSelectedMods) === JSON.stringify(payloadSelectedMods) &&
					item.idempotencyKey !== action.payload.idempotencyKey
				)
			})

			if (duplicateItemIndex !== -1) {
				// If we found a duplicate, update the existing item's quantity
				state.cart[duplicateItemIndex] = {
					...state.cart[duplicateItemIndex],
					qty: state.cart[duplicateItemIndex].qty + action.payload.qty,
					// Keep the original idempotencyKey and timeAdded
					idempotencyKey: state.cart[duplicateItemIndex].idempotencyKey,
					timeAdded: state.cart[duplicateItemIndex].timeAdded,
				}

				// Remove the newly updated item to prevent duplicates
				const updatedItemIndex = state.cart.findIndex(
					item => item.idempotencyKey === action.payload.idempotencyKey
				)
				if (updatedItemIndex !== -1) {
					state.cart.splice(updatedItemIndex, 1)
				}
				return
			}

			// If no duplicate found, proceed with normal update
			const index = state.cart.findIndex(
				item =>
					item.id === action.payload.id && item.idempotencyKey === action.payload.idempotencyKey
			)

			if (index !== -1) {
				state.cart[index] = {
					...state.cart[index],
					...action.payload,
					idempotencyKey: state.cart[index].idempotencyKey,
					timeAdded: state.cart[index].timeAdded,
				}
			}
		},
		removeItem: (state, action: PayloadAction<{ id: string; key: string }>) => {
			const index = state.cart.findIndex(
				item => item.id === action.payload.id && item.idempotencyKey === action.payload.key
			)
			if (index !== -1) {
				state.cart.splice(index, 1)
			}
		},
		addDeliveryEstimates: (state, action: PayloadAction<DeliveryEstimate[]>) => {
			state.deliveryEstimateList.push(...action.payload)
		},
		removeAllItems: state => {
			state.cart = []
		},
		resetDeliveryEstimateToDefault: state => {
			state.deliveryEstimate = {
				restaurantId: '',
				userAddress: null,
				estimate: {
					fee: -1,
					timeToDeliver: -1,
					totalFee: -1,
					gst: -1,
					pst: -1,
				},
			}
		},
		resetState: state => {
			Object.assign(state, initialState)
		},
		redeemReward: (
			state,
			action: PayloadAction<{
				rewardId: string
				pointsRedeemed: number
				totalDiscount: number
				restaurantId: string
				orderItemIdRedeemed: string
			}>
		) => {
			state.redeemedRewards.push(action.payload)
		},
		resetCartPrep: (
			state,
			action: PayloadAction<{ localTimezone: string; getLongestPrepTime: number }>
		) => {
			const { localTimezone, getLongestPrepTime } = action.payload
			const currentTime = utcToZonedTime(new Date(), localTimezone)
			const pickupTime = add(currentTime, { minutes: getLongestPrepTime })
			const duration = intervalToDuration({
				start: currentTime,
				end: pickupTime,
			})

			state.cartPrep = {
				isScheduled: false,
				pickupTime: state.isDelivery ? '' : pickupTime.toISOString(),
				deliveryTime: state.isDelivery ? pickupTime.toISOString() : '',
				prepTimeDuration: `P${duration.days}DT${duration.hours}H${duration.minutes}M${duration.seconds}S`,
				longestPrepTime: getLongestPrepTime,
				deliveryPrepTime: 15,
				pickupScheduled: false,
				deliveryScheduled: false,
			}
		},
		resetCoupon: state => {
			state.couponCode = ''
			state.couponCodeDisplay = null
			state.couponCodeBackup = ''
			state.couponName = ''
			state.restaurantDiscountAmount = null
		},
	},
})

const {
	setStates,
	addItem,
	removeItem,
	resetState,
	removeAllItems,
	resetCartPrep,
	addDeliveryEstimates,
	redeemReward,
	resetDeliveryEstimateToDefault,
	updateItem,
	resetCoupon,
} = slice.actions

export const setPersistedState = (updates: Partial<StateTypes>) => setStates(updates)
export const addItemToCart = (item: Item) => addItem(item)
export const updateItemInCart = (item: Item) => updateItem(item)
export const removeItemFromCart = (id: string, key: string) => removeItem({ id, key })
export const clearCart = () => removeAllItems()
export const resetDeliveryEstimate = () => resetDeliveryEstimateToDefault()
export const resetPersistedState = () => resetState()
export const addNewDeliveryEstimates = (newEstimates: DeliveryEstimate[]) =>
	addDeliveryEstimates(newEstimates)
export const redeemLoyaltyReward = (
	rewardId: string,
	pointsRedeemed: number,
	totalDiscount: number,
	restaurantId: string,
	orderItemIdRedeemed: string
) => redeemReward({ rewardId, pointsRedeemed, totalDiscount, restaurantId, orderItemIdRedeemed })

export const resetCartPrepState = ({ localTimezone, getLongestPrepTime }) =>
	resetCartPrep({ localTimezone, getLongestPrepTime })
export const resetCouponState = () => resetCoupon()
export default slice.reducer
